import React, { useState } from 'react';
import styled from 'styled-components';
import { Buttons, CancelButton, ModalCard, DateTime, Button, Checkbox, Level } from '~/components';
import { colors, weights } from '~/styles';
import ReactMarkdown from 'react-markdown';
import { dateFormats } from '~/utils';
import { useHistory } from 'react-router';
import { Card } from '~/components/ModalCard';
import { useApi, useMember, useWorkspace } from '~/contexts';

const Item = styled.div`
  flex: 1;
  padding-top: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid ${colors.grey10};

  &:last-child {
    border-bottom: none;
  }
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: ${weights.medium};
  text-align: left;
  padding-top: 2px;
  padding-bottom: 2px;
`;

const Content = styled.div`
  font-size: 1rem;
  font-weight: ${weights.light};
  text-align: justify;
  padding-top: 2px;
  padding-bottom: 2px;
  white-space: pre-wrap;

  ul {
    margin-top: -0.5rem;
    padding-left: 1.5rem;
    list-style-type: disc;
    line-height: 1rem;
  }

  li {
    margin-bottom: 0.25rem;
    line-height: 1.5rem;
  }
`;

const Image = styled.img`
  display: block;
  margin: 2rem auto;
  border-radius: 6px;
  box-shadow: 0px 3px 45px 0px ${colors.grey10};
`;

const ModalBody = styled(ModalCard.Body)`
  display: flex;
  flex-direction: column;
  max-height: 70vh;
  overflow-y: auto;
  padding: 0rem 1.625rem 1.625rem 1.625rem;
`;

const StyledModal = styled(ModalCard)`
  ${Card} {
    min-width: 54rem;
    max-width: 70vw;
  }
`;

function WhatsNewModal({ whatsNewEntries, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const { updateMember } = useMember();
  const history = useHistory();
  const [showWhatsNewModal, setShowWhatsNewModal] = useState(true);

  const handleClose = async () => {
    onClose();
    if (!showWhatsNewModal) {
      try {
        const { data } = await api.www.workspaces(workspace.id).members().setShowWhatsNewModal();
        updateMember(data);
      } catch {
        // Do nothing
      }
    }
  };

  function preserveBlankLines(text) {
    return text.replace(/(?<=\n\n)(?![*-])\n/gi, '&nbsp;\n ');
  }

  if (!whatsNewEntries) return null;

  return (
    <StyledModal title="What's New in Ruddr" onClose={handleClose}>
      <ModalBody>
        {whatsNewEntries.map((item) => (
          <Item key={item.id}>
            <DateTime
              value={item.date}
              format={dateFormats.longDate}
              style={{ color: colors.grey40, fontSize: '0.825rem' }}
              locale="en-US"
            />
            <Title>{item.title}</Title>
            <Content>
              <ReactMarkdown>{preserveBlankLines(item.content)}</ReactMarkdown>
            </Content>
            {item.imageUrl && (
              <Image
                src={item.imageUrl}
                alt={item.title}
                width={item.imageWidthPercent != null ? `${item.imageWidthPercent}%` : undefined}
              />
            )}
          </Item>
        ))}
      </ModalBody>
      <ModalCard.Footer>
        <Level>
          <Level.Item>
            <Checkbox
              name="showWhatsNewModal"
              label="Show new updates upon login"
              checked={showWhatsNewModal}
              onChange={() => setShowWhatsNewModal(!showWhatsNewModal)}
            />
          </Level.Item>

          <Level.Item>
            <Buttons align="right">
              <CancelButton onClick={handleClose}>Close</CancelButton>
              <Button
                onClick={() => {
                  handleClose();
                  history.replace({ pathname: '/whats-new' });
                }}>
                View All Updates
              </Button>
            </Buttons>
          </Level.Item>
        </Level>
      </ModalCard.Footer>
    </StyledModal>
  );
}

export default WhatsNewModal;
